import React, { useState, useMemo, useEffect } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, isTaskOverdue, enrichClientRow } from '../../core/helpers';
import { sb } from '../../core/db';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES, DEFAULT_DOC_TEMPLATES } from '../../core/constants';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { IconProperties } from '../../icons.properties';
import { Icon } from '../../components/Icon';
import { TasksTable } from '../tasks/TasksPage';
import { ClientDetail } from '../clients/ClientDetail';
import { useCounters, counterVal } from '../../core/useCounters';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { buildOrderParam } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const EMP_CLIENT_ORDER = { name: 'name', billing: 'total_billing', collected: 'collected', pending: 'pending' };



function EmpDashboard() {
  const { user, tasks, clients, invoices, tickets, setView, submissions, openModal, payments } = useApp();
  const counters = useCounters();
  const [drill, setDrill] = useState(null);
  const safeClients = clients || [];
  const safeInvoices = invoices || [];
  const safeTasks = tasks || [];
  const safePayments = payments || [];
  const myClients = useMemo(
    () => safeClients.filter(c => c.assignedTo === user.id).map(c => enrichClientRow(c, safeInvoices, safeTasks, safePayments)),
    [safeClients, user.id, safeInvoices, safeTasks, safePayments],
  );
  const myClientIds = myClients.map(c => c.id);
  const myTasks = safeTasks.filter(t => t.assignedTo === user.id || myClientIds.includes(t.clientId));
  const myTickets = (tickets || []).filter(t => t.assignedTo === user.id || t.raisedBy === user.id);
  const openTickets = myTickets.filter(t => t.status === "open" || t.status === "in_progress");
  const pendingReview = safeTasks.filter(t => t.assignedTo === user.id && t.status === "team_approval");
  const overdueList = myTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed");
  const overdue = overdueList.length;
  const myBilling = myClients.reduce((s, c) => s + c.totalBilling, 0);
  const myCollected = myClients.reduce((s, c) => s + c.collected, 0);

  return (
    <>
      <div className="stat-cards dashboard-stat-cards" style={{ marginBottom: 22 }}>
        {[
          { id: "clients", icon: "company", label: "My Clients", val: myClients.length, color: "var(--blue)" },
          { id: "pending", icon: "hourglass", label: "Pending", val: counterVal(counters, `pending_tasks:emp:${user.id}`), color: "var(--orange)" },
          { id: "completed", icon: "check", label: "Completed", val: counterVal(counters, `completed_tasks:emp:${user.id}`), color: "var(--green)" },
          { id: "overdue", icon: "warning", label: "Overdue", val: overdue, color: "var(--red)" },
        ].map(s => (
          <div key={s.id} className="stat-box" onClick={() => setDrill({ type: s.id, title: s.label })}>
            <div className="stat-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", background: `${s.color}15`, color: s.color, marginBottom: 12 }}><Icon name={s.icon} size={18} strokeWidth={2} /></div>
            <div className="stat-lbl">{s.label}</div>
            <div className="stat-val" style={{ color: s.color, fontSize: 22 }}>{s.val}</div>
            <div className="stat-drill">Click to expand →</div>
          </div>
        ))}
      </div>
      <div className="grid2">
        <div className="card">
          <div className="card-head"><div className="card-title">My Billing</div></div>
          <div className="card-body">
            {[["Total Billed", INR(myBilling), "var(--ink)"], ["Collected", INR(myCollected), "var(--green)"], ["Pending", INR(myBilling - myCollected), "var(--red)"]].map(([l, v, c]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F3F4F6" }}>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>{l}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-head"><div className="card-title">Pending Tasks</div></div>
          <div>
            {myTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").slice(0, 5).map(t => (
              <div key={t.id} className="row-item">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</div>
                  <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.clientName}</div>
                </div>
                <Badge status={t.status} />
              </div>
            ))}
            {myTasks.filter(t => t.status !== "completed").length === 0 && <div className="empty"><div className="empty-icon"><Icon name="checkCircle2" size={28} /></div><div>All caught up!</div></div>}
          </div>
        </div>
      </div>
      {drill && <EmpDrill type={drill.type} title={drill.title} onClose={() => setDrill(null)} />}
    </>
  );
}

function EmpDrill({ type, title, onClose }) {
  const { user, tasks, clients, invoices, payments } = useApp();
  const myClients = useMemo(
    () => (clients || []).filter(c => c.assignedTo === user.id).map(c => enrichClientRow(c, invoices || [], tasks || [], payments || [])),
    [clients, user.id, invoices, tasks, payments],
  );
  const myClientIds = myClients.map(c => c.id);
  const myTasks = (tasks || []).filter(t => t.assignedTo === user.id || myClientIds.includes(t.clientId));

  let content;
  if (type === "clients") {
    content = myClients.length ? (
      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Client</th><th>Type</th><th>Billing</th><th>Collected</th><th>Pending</th></tr></thead>
          <tbody>
            {myClients.map(c => (
              <tr key={c.id}>
                <td><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></td>
                <td><span className="tag">{c.type}</span></td>
                <td style={{ fontWeight: 700 }}>{INR(c.totalBilling)}</td>
                <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(c.collected)}</td>
                <td style={{ color: c.pending > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{INR(c.pending)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="empty"><div className="empty-icon"><Icon name="company" size={28} /></div><div style={{ fontWeight: 600 }}>No clients assigned to you yet</div></div>
    );
  } else {
    const list = type === "pending" ? myTasks.filter(t => t.status !== "completed" && t.status !== "cancelled")
      : type === "completed" ? myTasks.filter(t => t.status === "completed")
        : type === "overdue" ? myTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed")
          : myTasks;
    content = <TasksTable tasks={list} showClient={true} />;
  }

  return (
    <div className="drill-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="drill-panel">
        <div className="drill-head">
          <div className="drill-title">{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }}><Icon name="close" size={20} /></button>
        </div>
        <div className="drill-body">{content}</div>
      </div>
    </div>
  );
}

function EmpTasks() {
  const { user, tasks, setTasks, openModal, submissions } = useApp();
  const myTasks = tasks.filter(t => t.assignedTo === user.id);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const visible = (filter === "all" ? myTasks : myTasks.filter(t => t.status === filter))
    .filter(t => !search || (t.title || "").toLowerCase().includes(search.toLowerCase()) || (t.clientName || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="stat-cards" style={{ marginBottom: 18 }}>
        {[
          ["Pending", myTasks.filter(t => t.status !== "completed" && t.status !== "cancelled").length, "var(--orange)"],
          ["Completed", myTasks.filter(t => t.status === "completed").length, "var(--green)"],
          ["Overdue", myTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed").length, "var(--red)"],
          ["Total", myTasks.length, "var(--ink)"],
        ].map(([l, v, c]) => (
          <div key={l} className="stat-box"><div className="stat-lbl">{l}</div><div className="stat-val" style={{ color: c, fontSize: 22 }}>{v}</div></div>
        ))}
      </div>
      <div className="chips">
        <div className={`chip ${filter === "all" ? "on" : ""}`} onClick={() => setFilter("all")}>All ({myTasks.length})</div>
        {TASK_STATUSES.map(s => (
          <div key={s.id} className={`chip ${filter === s.id ? "on" : ""}`} onClick={() => setFilter(s.id)}>
            {s.label} ({myTasks.filter(t => t.status === s.id).length})
          </div>
        ))}
      </div>
      <FilterBar search={search} setSearch={setSearch} searchPlaceholder="Search task / client…" onClear={() => setSearch("")} />
      <TasksTable tasks={visible} showClient={true} />
    </>
  );
}

function EmpClients() {
  const { user, clients, tasks, employees, openModal, invoices, payments, notifFocus, setNotifFocus } = useApp();
  const safeInvoices = invoices || [];
  const safeTasks = tasks || [];
  const safePayments = payments || [];
  const myClients = useMemo(
    () => (clients || []).filter(c => c.assignedTo === user.id).map(c => enrichClientRow(c, safeInvoices, safeTasks, safePayments)),
    [clients, user.id, safeInvoices, safeTasks, safePayments],
  );
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (!notifFocus?.clientId) return;
    const c = clients.find(x => x.id === notifFocus.clientId);
    if (c && c.assignedTo === user.id) setSelected(c);
    setNotifFocus(null);
  }, [notifFocus?.clientId, clients, user.id, setNotifFocus]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const localVisible = myClients.filter(c =>
    !search || (c.name || "").toLowerCase().includes(search.toLowerCase()) || (c.clientNo || "").includes(search)
  );
  const apiFilter = buildApiFilter({
    equals: { assigned_to: user.id },
    search,
    searchFields: ["name", "client_no"],
    order: buildOrderParam(sortKey, sortDir, EMP_CLIENT_ORDER) || "created_at.desc",
  });
  const { rows: visibleRaw = [], hasMore, loadMore, loadingMore } = usePaginatedApiRows("clients", apiFilter, localVisible, { enabled: !!apiFilter, rollupInvoices: safeInvoices, rollupTasks: safeTasks, rollupPayments: safePayments });

  const visible = useMemo(
    () => (visibleRaw || []).map(c => enrichClientRow(c, safeInvoices, safeTasks, safePayments)),
    [visibleRaw, safeInvoices, safeTasks, safePayments],
  );

  if (selected) return <ClientDetail client={selected} onBack={() => setSelected(null)} />;

  return (
    <>
      <div className="list-toolbar">
        <div className="list-toolbar-search">
          <input className="f-input" placeholder="Search client…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="list-toolbar-actions">
          <button type="button" className="btn btn-primary"
            onClick={() => openModal("create-client", { defaultAssignTo: user.id })}>
            + New Client
          </button>
        </div>
      </div>
      <div className="card">
        <div className="tbl-wrap">
          <table className="tbl-clients">
            <thead><tr>
              <SortableTh label="Client" sortKey="name" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th>Type</th><th>Contact</th>
              <SortableTh label="Billing" sortKey="billing" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Collected" sortKey="collected" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Pending" sortKey="pending" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th>Progress</th><th></th>
            </tr></thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={8}><div className="empty"><div className="empty-icon"><Icon name="company" size={28} /></div><div>No clients assigned to you yet</div></div></td></tr>
              )}
              {visible.map(c => {
                const clientTasks = tasks.filter(t => t.clientId === c.id);
                const done = clientTasks.filter(t => t.status === "completed").length;
                const progress = clientTasks.length ? Math.round(done / clientTasks.length * 100) : c.progress;
                return (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => setSelected(c)}>
                    <td><div style={{ fontWeight: 600 }}>{c.name}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.clientNo}</div></td>
                    <td><span className="tag">{c.type}</span></td>
                    <td><div style={{ fontSize: 12 }}>{c.contactName}</div><div style={{ fontSize: 11, color: "var(--muted)" }}>{c.phone}</div></td>
                    <td style={{ fontWeight: 700 }}>{INR(c.totalBilling)}</td>
                    <td style={{ color: "var(--green)", fontWeight: 600 }}>{INR(c.collected)}</td>
                    <td style={{ color: c.pending > 0 ? "var(--red)" : "var(--muted)", fontWeight: 600 }}>{INR(c.pending)}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 100 }}>
                        <div className="prog-bg" style={{ flex: 1 }}><div className="prog-fill" style={{ width: progress + "%", background: progress === 100 ? "var(--green)" : "var(--blue)" }} /></div>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{progress}%</span>
                      </div>
                    </td>
                    <td><button className="btn btn-sm">View →</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreBar hasMore={hasMore} loadingMore={loadingMore} onLoadMore={loadMore} count={visible.length} />
      </div>
    </>
  );
}

export { EmpDashboard, EmpTasks, EmpClients };
